import React, { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
import { FaTimes, FaSave, FaPlus, FaTrash, FaLink, FaUnlink, FaArrowLeft, FaHeart, FaUserPlus } from 'react-icons/fa';
import { useModal } from '../../context/ModalContext';
import AddLeadModal from '../crm/AddLeadModal';

/* ── Phone helpers ────────────────────────────────────────────── */

function normalizePhone(str) {
  if (!str) return '';
  return String(str).replace(/@s\.whatsapp\.net|@g\.us|@lid/g, '').replace(/\D/g, '');
}

function formatPhone(phone) {
  const clean = normalizePhone(phone);
  if (!clean) return '';
  if (clean.length >= 10) {
    const cc = clean.slice(0, clean.length - 10);
    const num = clean.slice(-10);
    return cc ? `+${cc} ${num.slice(0, 5)} ${num.slice(5)}` : num;
  }
  return clean;
}

/* ── Constants ────────────────────────────────────────────────── */

const STAGES = ['new', 'contacted', 'qualified', 'converted', 'lost'];
const STAGE_LABELS = { new: 'New', contacted: 'Contacted', qualified: 'Qualified', converted: 'Converted', lost: 'Lost' };
const TYPE_COLORS = {
  stage_change: 'bg-purple-500',
  note_added: 'bg-amber-500',
  flow_assigned: 'bg-blue-500',
  flow_removed: 'bg-red-500',
  created: 'bg-green-500',
  merged: 'bg-teal-500',
  updated: 'bg-gray-400',
};

/* ── Time helper ──────────────────────────────────────────────── */

function timeAgo(date) {
  const now = new Date();
  const past = new Date(date);
  const diffMs = now - past;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);
  const diffWeek = Math.floor(diffDay / 7);
  const diffMonth = Math.floor(diffDay / 30);
  const diffYear = Math.floor(diffDay / 365);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin} min${diffMin !== 1 ? 's' : ''} ago`;
  if (diffHr < 24) return `${diffHr} hour${diffHr !== 1 ? 's' : ''} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;
  if (diffWeek < 5) return `${diffWeek} week${diffWeek !== 1 ? 's' : ''} ago`;
  if (diffMonth < 12) return `${diffMonth} month${diffMonth !== 1 ? 's' : ''} ago`;
  return `${diffYear} year${diffYear !== 1 ? 's' : ''} ago`;
}

/* ── Shared input class ───────────────────────────────────────── */

const INPUT_CLS = 'w-full text-sm rounded-lg border border-surface-border px-3 py-2 outline-none focus:border-brand/40 transition-colors';

/* ── Component ────────────────────────────────────────────────── */

export default function UniboxDetail({ contact, onClose, onUpdate, flows, isMobile }) {
  const modal = useModal();

  // Lead data
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(false);

  // Editable fields (separate first/last name)
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [localTags, setLocalTags] = useState([]);
  const [localCustomFields, setLocalCustomFields] = useState([]);

  // New-item inputs
  const [newTag, setNewTag] = useState('');
  const [newFieldKey, setNewFieldKey] = useState('');
  const [newFieldValue, setNewFieldValue] = useState('');

  // Flow
  const [selectedFlowId, setSelectedFlowId] = useState('');

  // Promote modal
  const [showPromoteModal, setShowPromoteModal] = useState(false);

  /* ── Fetch contact (refresh after updates) ──────────────────── */

  const fetchContact = async () => {
    if (!contact?.id) return;
    try {
      const res = await apiGet('/api/contacts/' + contact.id);
      const data = res?.contact || null;
      if (data) {
        setLead(data);
        setEditFirstName(data.firstName || '');
        setEditLastName(data.lastName || '');
        setEditEmail(data.email || '');
        setEditNotes(data.notes || '');
        setLocalTags(data.tags || []);
        setLocalCustomFields(data.customFields || []);
      } else {
        setLead(null);
      }
    } catch {
      setLead(null);
    }
  };

  useEffect(() => {
    if (contact?.isLead && contact?.id) {
      setLoading(true);
      apiGet('/api/contacts/' + contact.id)
        .then((res) => {
          const data = res?.contact || null;
          if (data) {
            setLead(data);
            setEditFirstName(data.firstName || '');
            setEditLastName(data.lastName || '');
            setEditEmail(data.email || '');
            setEditNotes(data.notes || '');
            setLocalTags(data.tags || []);
            setLocalCustomFields(data.customFields || []);
          } else {
            setLead(null);
          }
        })
        .catch(() => setLead(null))
        .finally(() => setLoading(false));
    } else {
      setLead(null);
    }
  }, [contact?.id, contact?.isLead]);

  /* ── Field save handlers ────────────────────────────────────── */

  const handleSaveFirstName = async () => {
    if (!lead || editFirstName === (lead.firstName || '')) return;
    await apiPut('/api/contacts/' + lead.id, { firstName: editFirstName });
    fetchContact();
  };

  const handleSaveLastName = async () => {
    if (!lead || editLastName === (lead.lastName || '')) return;
    await apiPut('/api/contacts/' + lead.id, { lastName: editLastName });
    fetchContact();
  };

  const handleSaveEmail = async () => {
    if (!lead || editEmail === lead.email) return;
    await apiPut('/api/contacts/' + lead.id, { email: editEmail });
    fetchContact();
  };

  const handleSaveNotes = async () => {
    if (!lead) return;
    await apiPut('/api/contacts/' + lead.id, { notes: editNotes });
    fetchContact();
  };

  const handleStageChange = async (value) => {
    if (!lead) return;
    await apiPut('/api/contacts/' + lead.id, { stage: value });
    fetchContact();
    onUpdate?.();
  };

  /* ── Tags ────────────────────────────────────────────────────── */

  const handleAddTag = async (e) => {
    if (e.key !== 'Enter' || !newTag.trim()) return;
    const updated = [...localTags, newTag.trim()];
    setLocalTags(updated);
    setNewTag('');
    await apiPut('/api/contacts/' + lead.id, { tags: updated });
    fetchContact();
  };

  const handleRemoveTag = async (idx) => {
    const updated = localTags.filter((_, i) => i !== idx);
    setLocalTags(updated);
    await apiPut('/api/contacts/' + lead.id, { tags: updated });
    fetchContact();
  };

  /* ── Custom fields ──────────────────────────────────────────── */

  const handleSaveCustomFields = async () => {
    if (!lead) return;
    await apiPut('/api/contacts/' + lead.id, { customFields: localCustomFields });
    fetchContact();
  };

  const handleAddField = () => {
    if (!newFieldKey.trim()) return;
    setLocalCustomFields([...localCustomFields, { key: newFieldKey.trim(), value: newFieldValue }]);
    setNewFieldKey('');
    setNewFieldValue('');
  };

  const handleRemoveField = async (idx) => {
    const updated = localCustomFields.filter((_, i) => i !== idx);
    setLocalCustomFields(updated);
    await apiPut('/api/contacts/' + lead.id, { customFields: updated });
    fetchContact();
  };

  /* ── Flow Assignment ────────────────────────────────────────── */

  const handleAssignFlow = async () => {
    if (!lead || !selectedFlowId) return;
    await apiPost('/api/contacts/' + lead.id + '/assign-flow', { flowId: selectedFlowId });
    setSelectedFlowId('');
    fetchContact();
    onUpdate?.();
  };

  const handleRemoveFlow = async () => {
    if (!lead) return;
    await apiPost('/api/contacts/' + lead.id + '/remove-flow', {});
    fetchContact();
    onUpdate?.();
  };

  /* ── Delete ─────────────────────────────────────────────────── */

  const handleDelete = async () => {
    const confirmed = await modal.confirm('Are you sure you want to delete this lead? This action cannot be undone.');
    if (!confirmed) return;
    await apiDelete('/api/contacts/' + lead.id);
    onClose?.();
    onUpdate?.();
  };

  /* ── Promote to lead ────────────────────────────────────────── */

  /* ── Derived data ───────────────────────────────────────────── */

  const containerClass = isMobile
    ? 'fixed inset-0 z-50 bg-surface-card flex flex-col'
    : 'w-[400px] h-full bg-surface-card border-l border-surface-border flex flex-col flex-shrink-0 overflow-hidden';

  const sortedActivity = lead?.activity
    ? [...lead.activity].sort((a, b) => new Date(b.timestamp || b.date || b.createdAt || 0) - new Date(a.timestamp || a.date || a.createdAt || 0)).slice(0, 50)
    : [];

  const assignedFlow = lead?.assignedFlowId
    ? (typeof lead.assignedFlowId === 'object'
        ? lead.assignedFlowId
        : flows?.find((f) => f.id === lead.assignedFlowId) || null)
    : null;

  /* ── Render ─────────────────────────────────────────────────── */

  return (
    <div className={containerClass}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-surface-border flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          {isMobile && (
            <button onClick={onClose} className="text-ink-secondary hover:text-ink-primary">
              <FaArrowLeft />
            </button>
          )}
          <h2 className="text-base font-semibold text-ink-primary">
            {contact?.isLead && lead ? 'Lead Details' : 'Contact Info'}
          </h2>
        </div>
        <button onClick={onClose} className="text-ink-secondary hover:text-ink-primary">
          <FaTimes />
        </button>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto">
        {loading && (
          <div className="px-4 py-8 text-center text-ink-muted text-sm">Loading...</div>
        )}

        {/* ── Lead view ──────────────────────────────────────── */}
        {!loading && contact?.isLead && lead && (
          <>
            {/* Profile Card: Avatar + Name + Bio */}
            <div className="px-4 py-4 border-b border-surface-border">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-14 h-14 rounded-full flex-shrink-0 overflow-hidden bg-surface-bg border border-surface-border flex items-center justify-center">
                  {(lead.avatarUrl || contact?.avatarUrl) ? (
                    <img src={lead.avatarUrl || contact?.avatarUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-lg font-semibold text-ink-faint">
                      {(lead.firstName || lead.pushName || normalizePhone(lead.phone) || '?').substring(0, 2).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="text-base font-semibold text-ink-primary truncate">
                    {[lead.firstName, lead.lastName].filter(Boolean).join(' ') || lead.pushName || formatPhone(lead.phone)}
                  </div>
                  {lead.pushName && lead.firstName && lead.pushName !== lead.firstName && (
                    <div className="text-xs text-ink-muted truncate">~{lead.pushName}</div>
                  )}
                  {lead.bio && (
                    <div className="text-xs text-ink-secondary mt-0.5 italic truncate">{lead.bio}</div>
                  )}
                  {lead.company && (
                    <div className="text-xs text-ink-muted mt-0.5 truncate">{lead.company}</div>
                  )}
                </div>
              </div>
            </div>

            {/* First Name / Last Name */}
            <div className="px-4 py-3 border-b border-surface-border">
              <div className="flex gap-2">
                <div className="flex-1">
                  <span className="text-[10px] text-ink-faint uppercase tracking-wide">First Name</span>
                  <input
                    type="text"
                    value={editFirstName}
                    onChange={(e) => setEditFirstName(e.target.value)}
                    onBlur={handleSaveFirstName}
                    className="text-base font-semibold border-none bg-transparent w-full outline-none text-ink-primary"
                    placeholder="First name"
                  />
                </div>
                <div className="flex-1">
                  <span className="text-[10px] text-ink-faint uppercase tracking-wide">Last Name</span>
                  <input
                    type="text"
                    value={editLastName}
                    onChange={(e) => setEditLastName(e.target.value)}
                    onBlur={handleSaveLastName}
                    className="text-base font-semibold border-none bg-transparent w-full outline-none text-ink-primary"
                    placeholder="Last name"
                  />
                </div>
              </div>
            </div>

            {/* Phone */}
            <div className="px-4 py-3 border-b border-surface-border">
              <span className="text-xs text-ink-faint">Phone</span>
              <div className="text-sm text-ink-muted">{formatPhone(lead.phone)}</div>
            </div>

            {/* Email */}
            <div className="px-4 py-3 border-b border-surface-border">
              <span className="text-xs text-ink-faint">Email</span>
              <input
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                onBlur={handleSaveEmail}
                className="w-full text-sm border-none bg-transparent outline-none text-ink-primary"
                placeholder="Add email"
              />
            </div>

            {/* Source */}
            {lead.source && (
              <div className="px-4 py-3 border-b border-surface-border">
                <span className="text-xs text-ink-faint">Source</span>
                <div className="mt-1">
                  <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-surface-bg border border-surface-border text-ink-secondary">
                    {lead.source}
                  </span>
                </div>
              </div>
            )}

            {/* Stage */}
            <div className="px-4 py-3 border-b border-surface-border">
              <label className="text-xs font-medium text-ink-faint uppercase tracking-wide mb-1 block">Stage</label>
              <select
                value={lead.stage || 'new'}
                onChange={(e) => handleStageChange(e.target.value)}
                className={INPUT_CLS + ' bg-surface-bg text-ink-primary'}
              >
                {STAGES.map((s) => (
                  <option key={s} value={s}>{STAGE_LABELS[s]}</option>
                ))}
              </select>
            </div>

            {/* Tags */}
            <div className="px-4 py-3 border-b border-surface-border">
              <label className="text-xs font-medium text-ink-faint uppercase tracking-wide mb-2 block">Tags</label>
              <div className="flex flex-wrap gap-1 mb-2">
                {localTags.map((tag, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-surface-bg border border-surface-border text-ink-secondary"
                  >
                    {tag}
                    <button onClick={() => handleRemoveTag(i)} className="text-ink-faint hover:text-ink-primary">
                      <FaTimes size={8} />
                    </button>
                  </span>
                ))}
              </div>
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={handleAddTag}
                placeholder="Add tag and press Enter"
                className={INPUT_CLS + ' text-xs bg-surface-bg text-ink-primary'}
              />
            </div>

            {/* Custom Fields */}
            <div className="px-4 py-3 border-b border-surface-border">
              <label className="text-xs font-medium text-ink-faint uppercase tracking-wide mb-2 block">Custom Fields</label>
              <div className="space-y-2">
                {localCustomFields.map((field, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={field.key}
                      onChange={(e) => {
                        const updated = [...localCustomFields];
                        updated[i] = { ...updated[i], key: e.target.value };
                        setLocalCustomFields(updated);
                      }}
                      onBlur={handleSaveCustomFields}
                      className="w-1/3 text-xs border border-surface-border rounded-md px-2 py-1.5 bg-surface-bg text-ink-primary outline-none"
                      placeholder="Key"
                    />
                    <input
                      type="text"
                      value={field.value}
                      onChange={(e) => {
                        const updated = [...localCustomFields];
                        updated[i] = { ...updated[i], value: e.target.value };
                        setLocalCustomFields(updated);
                      }}
                      onBlur={handleSaveCustomFields}
                      className="flex-1 text-xs border border-surface-border rounded-md px-2 py-1.5 bg-surface-bg text-ink-primary outline-none"
                      placeholder="Value"
                    />
                    <button onClick={() => handleRemoveField(i)} className="text-ink-faint hover:text-red-500">
                      <FaTrash size={12} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="text"
                  value={newFieldKey}
                  onChange={(e) => setNewFieldKey(e.target.value)}
                  placeholder="Key"
                  className="w-1/3 text-xs border border-surface-border rounded-md px-2 py-1.5 bg-surface-bg text-ink-primary outline-none"
                />
                <input
                  type="text"
                  value={newFieldValue}
                  onChange={(e) => setNewFieldValue(e.target.value)}
                  placeholder="Value"
                  className="flex-1 text-xs border border-surface-border rounded-md px-2 py-1.5 bg-surface-bg text-ink-primary outline-none"
                />
                <button onClick={handleAddField} className="text-ink-faint hover:text-ink-primary">
                  <FaPlus size={12} />
                </button>
              </div>
            </div>

            {/* Notes */}
            <div className="px-4 py-3 border-b border-surface-border">
              <label className="text-xs font-medium text-ink-faint uppercase tracking-wide mb-1 block">Notes</label>
              <textarea
                rows={3}
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                className={INPUT_CLS + ' bg-surface-bg text-ink-primary resize-none'}
                placeholder="Add notes..."
              />
              <button
                onClick={handleSaveNotes}
                className="btn-secondary text-xs mt-1 flex items-center gap-1"
              >
                <FaSave size={10} /> Save Notes
              </button>
            </div>

            {/* Flow Assignment */}
            <div className="px-4 py-3 border-b border-surface-border">
              <label className="text-xs font-medium text-ink-faint uppercase tracking-wide mb-1 block">Flow</label>
              {assignedFlow ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-ink-primary">
                    <FaLink size={12} className="text-ink-faint" />
                    {assignedFlow.name}
                  </div>
                  <button
                    onClick={handleRemoveFlow}
                    className="text-xs text-red-500 hover:text-red-600 flex items-center gap-1"
                  >
                    <FaUnlink size={10} /> Remove
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <select
                    value={selectedFlowId}
                    onChange={(e) => setSelectedFlowId(e.target.value)}
                    className="flex-1 text-sm border border-surface-border rounded-md px-2 py-1.5 bg-surface-bg text-ink-primary outline-none"
                  >
                    <option value="">Select a flow</option>
                    {flows?.map((f) => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                  <button
                    onClick={handleAssignFlow}
                    disabled={!selectedFlowId}
                    className="btn-secondary text-xs flex items-center gap-1"
                  >
                    <FaLink size={10} /> Assign
                  </button>
                </div>
              )}
            </div>

            {/* Activity Timeline */}
            {sortedActivity.length > 0 && (
              <div className="px-4 py-3 border-b border-surface-border">
                <label className="text-xs font-medium text-ink-faint uppercase tracking-wide mb-2 block">Activity</label>
                <div className="space-y-3">
                  {sortedActivity.map((entry, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <div
                        className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${TYPE_COLORS[entry.type] || 'bg-gray-400'}`}
                      />
                      <div className="min-w-0">
                        <div className="text-xs text-ink-secondary">{entry.description}</div>
                        <div className="text-[10px] text-ink-faint">
                          {timeAgo(entry.timestamp || entry.date || entry.createdAt)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Delete */}
            <div className="px-4 py-3 border-b border-surface-border">
              <button onClick={handleDelete} className="btn-danger w-full">
                Delete Lead
              </button>
            </div>
          </>
        )}

        {/* ── Non-lead / Promote to Lead ──────────────────── */}
        {!loading && (!contact?.isLead || !lead) && (
          <div className="px-4 py-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-surface-bg border border-surface-border flex items-center justify-center mx-auto mb-3 overflow-hidden">
                {contact?.avatarUrl ? (
                  <img src={contact.avatarUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xl font-semibold text-ink-faint">
                    {(contact?.firstName || contact?.pushName || normalizePhone(contact?.phone) || '?').substring(0, 2).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="text-lg font-medium text-ink-primary mb-1">
                {[contact?.firstName, contact?.lastName].filter(Boolean).join(' ') || contact?.pushName || formatPhone(contact?.phone)}
              </div>
              {contact?.pushName && contact?.firstName && contact.pushName !== contact.firstName && (
                <div className="text-xs text-ink-muted">~{contact.pushName}</div>
              )}
              {contact?.bio && (
                <div className="text-xs text-ink-secondary italic mt-1">{contact.bio}</div>
              )}
              <p className="text-xs text-ink-muted mt-2">This contact is not a lead yet.</p>
            </div>

            <button
              onClick={() => setShowPromoteModal(true)}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              <FaUserPlus size={12} />
              Promote to Lead
            </button>
          </div>
        )}
      </div>

      {/* Promote Modal (same form as Add Lead) */}
      {showPromoteModal && (
        <AddLeadModal
          onClose={() => setShowPromoteModal(false)}
          onCreated={() => {
            setShowPromoteModal(false);
            onUpdate?.();
          }}
          prefillPhone={normalizePhone(contact?.phone)}
          prefillName={[contact?.firstName, contact?.lastName].filter(Boolean).join(' ') || contact?.name || ''}
          contactId={contact?.id || null}
          sessionId={contact?.sessionId || ''}
        />
      )}
    </div>
  );
}
